// ABOUTME: blab-auth browser client, the currentToken() that supplies the app token, and token claims decoding
// ABOUTME: Local mode (VITE_SKIP_AUTH) skips blab-auth and sends no token

import { type BrowserAuth, createBrowserAuth } from '@boundarylab/auth/browser';

export const TOKEN_STORAGE_KEY = 'alpha_bearer_token';

export function createAlphaAuth(overrides: Partial<Parameters<typeof createBrowserAuth>[0]> = {}) {
  return createBrowserAuth({ app: 'alpha-finder', storageKey: TOKEN_STORAGE_KEY, ...overrides });
}

export const auth: BrowserAuth = createAlphaAuth();

export class NotSignedInError extends Error {}

export const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';

export function makeCurrentToken(a: BrowserAuth, skipAuth = SKIP_AUTH) {
  // forceRefresh mints a new token even while the stored one is live: the caller's server rejected it.
  return async function currentToken(opts: { forceRefresh?: boolean } = {}): Promise<string> {
    // Local mode: the local Workers run with SKIP_AUTH, and blab-auth can't be reached from localhost.
    if (skipAuth) return '';
    const s = opts.forceRefresh ? await a.refresh() : await a.session();
    if (s.status === 'signed_in') return s.token;
    if (s.status !== 'unavailable' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    throw new NotSignedInError(s.status);
  };
}

export const currentToken = makeCurrentToken(auth);

export interface TokenClaims {
  uid: string;
  email: string;
  roles: string[];
}

/** Read the claims of a token the api will verify; this does not verify it. */
export function decodeClaims(token: string): TokenClaims | null {
  try {
    const part = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(part.padEnd(Math.ceil(part.length / 4) * 4, '='));
    const json = JSON.parse(
      new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)))
    );
    if (typeof json.uid !== 'string') return null;
    return {
      uid: json.uid,
      email: typeof json.email === 'string' ? json.email : '',
      roles: Array.isArray(json.roles)
        ? json.roles.filter((r: unknown): r is string => typeof r === 'string')
        : [],
    };
  } catch {
    return null;
  }
}
